import jwt_decode from 'jwt-decode'
import { authApiHandler } from '../services/authApi'
import type {
  UserData,
  AuthConfig,
  Contact,
  IdTokenProfile,
  IssuerProvider,
  Profile,
  SourcingCompany,
} from '../types/types'
import type {
  AccessTokenContactData,
  AccessTokenData,
  IdTokenData,
  TokenData,
  ValidTokens,
  ZendeskTokenData,
} from '../types/tokenTypes'
import cookieHandler from './cookieHandler'
import { identityProviders } from '../constants'

const getValidToken = <T>(token: string, bufferInSeconds?: number): T | null => {
  return isValidToken(token, bufferInSeconds) ? getToken<T>(token) : null
}

export const getToken = <T>(token: string): T | null => {
  if (!token) return null

  try {
    const decodedToken = <T>jwt_decode(token)
    if (!decodedToken) return null

    return decodedToken
  } catch (error) {
    console.error(`Error trying to decode token: ${error}`)
    return null
  }
}

const isValidZendeskToken = (token: string, contactId: number, bufferInSeconds?: number) => {
  if (!token) return false

  try {
    const decodedToken = <ZendeskTokenData>jwt_decode(token)
    const expireWithBuffer = decodedToken.exp + (bufferInSeconds ?? 120)
    const issuedAtWithBuffer = decodedToken.iat - (bufferInSeconds ?? 120)
    const currentDate = Date.now() / 1000

    const isValid = expireWithBuffer > currentDate && issuedAtWithBuffer < currentDate

    const isContactValid = decodedToken.contact && decodedToken.contact.contact_id === contactId

    return isValid && isContactValid
  } catch (error) {
    console.error(`Error trying to parse token: ${error}`)
    return false
  }
}

const isValidToken = (token: string, bufferInSeconds?: number) => {
  if (!token) return false

  try {
    const decodedToken = <TokenData>jwt_decode(token)
    const expireWithBuffer = decodedToken.exp + (bufferInSeconds ?? 120)
    const issuedAtWithBuffer = decodedToken.iat - (bufferInSeconds ?? 120)
    const currentDate = Date.now() / 1000

    const isValid = expireWithBuffer > currentDate && issuedAtWithBuffer < currentDate

    return isValid
  } catch (error) {
    console.error(`Error trying to parse token: ${error}`)
    return false
  }
}

// Punchout id tokens have no preferred_username; page props cannot carry undefined.
const getIdTokenProfile = (idToken: IdTokenData): IdTokenProfile => ({
  userName: idToken.preferred_username ?? '',
  name: idToken.name ?? '',
  email: idToken.email ?? '',
})

const getProfile = (
  idTokenProfile: IdTokenProfile | null,
  accessToken: AccessTokenData
): Profile => {
  return {
    identityProvider: {
      name: accessToken.identity_provider.name,
      userId: accessToken.identity_provider.user_id,
    },
    userName: idTokenProfile?.userName ?? '',
    name: idTokenProfile?.name ?? '',
    email: idTokenProfile?.email ?? '',
    userIdentifier:
      accessToken.identity_provider.name === identityProviders.okta
        ? accessToken.useridentifier
        : '',
  }
}

const getContact = (
  accessTokenContactData: AccessTokenContactData | null | undefined
): Contact | null => {
  return accessTokenContactData
    ? {
        id: accessTokenContactData.contact_id,
        customerId: accessTokenContactData.customer_id,
        customerErp: accessTokenContactData.customer_erp,
        siteType: accessTokenContactData.is_b2b ? 'Business' : 'Consumer',
        sourcingCompany: getSourcingCompanyFromSourcingCompanyEnum(
          accessTokenContactData.sourcing_company
        ),
      }
    : null
}

const getSourcingCompanyFromSourcingCompanyEnum = (
  sourcingCompany: number
): SourcingCompany | null => {
  switch (sourcingCompany) {
    case 1:
      return 'SE'
    case 2:
      return 'DK'
    case 3:
      return 'NO'
    case 4:
      return 'FI'
    case 5:
      return 'NL'
    case 6:
      return 'BE'
    default:
      return null
  }
}

export default (config: AuthConfig) => {
  const cookie = cookieHandler()
  const authApi = authApiHandler(config)

  const getProvider = (token: TokenData): IssuerProvider | null => {
    switch (token.iss) {
      case config.oktaOpenIdIssuer:
        return 'okta'
      case config.azureAdOpenIdIssuer:
        return 'azureAd'
      case config.dustinOpenIdIssuer:
        return 'dustinPunchout'
      default:
        return null
    }
  }

  const getValidTokens = async (
    idToken: string,
    refreshToken: string,
    accessToken?: string
  ): Promise<ValidTokens | null> => {
    let validIdTokenData = getValidToken<IdTokenData>(idToken)
    let validAccessTokenData = accessToken ? getValidToken<AccessTokenData>(accessToken) : null

    if (!validIdTokenData && !refreshToken) {
      return null
    }

    const selectedContactId = cookie.getContactId()

    if (!validIdTokenData && !!refreshToken) {
      const provider = cookie.getProvider()

      const tokens = await authApi.refreshTokens(refreshToken, provider, selectedContactId)

      if (!tokens) return null

      refreshToken = tokens.refreshToken
      idToken = tokens.idToken
      validIdTokenData = getValidToken(idToken)
      validAccessTokenData = accessToken ? getValidToken(accessToken) : null

      if (!validIdTokenData || !validAccessTokenData) return null
    }

    if (!validAccessTokenData) {
      const newAccessToken = await authApi.getAccessToken(idToken, selectedContactId)
      if (!newAccessToken) return null
      accessToken = newAccessToken
      validAccessTokenData = getValidToken(accessToken)
    }

    if (!validIdTokenData || !validAccessTokenData || !accessToken) return null

    return {
      idToken,
      micrositeToken: accessToken,
      refreshToken,
      validAccessTokenData,
      validIdTokenData,
    }
  }

  const getUserData = (
    idTokenProfile: IdTokenProfile | null,
    accessToken: AccessTokenData
  ): UserData => {
    return {
      profile: getProfile(idTokenProfile, accessToken),
      contact: getContact(accessToken.contact),
      allowImpersonateContact: accessToken.allow_impersonate_contact,
      isPunchout: accessToken.is_punchout,
      hasMultipleB2BContacts: accessToken.has_multiple_b2b_contacts,
      isFederated: accessToken.is_federated_partner,
    }
  }

  return {
    getProvider,
    isValidToken,
    isValidZendeskToken,
    getValidToken,
    getValidTokens,
    getToken,
    getProfile,
    getIdTokenProfile,
    getContact,
    getUserData,
  }
}
